import React, { useState, useEffect, useCallback } from 'react';
import { ProgressBar } from '@shopify/polaris';
// Icons removed as they were unused - can add back if needed for future enhancements
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../hooks/useCurrency';

const STORAGE_KEY = 'nudgesmart_trial_status_v1';

/**
 * Trial phase types for different UI states
 */
const TRIAL_PHASES = {
  EARLY: 'early_trial',      // Days 1-3
  MID: 'mid_trial',          // Days 4-10
  ENDING: 'ending_soon',     // Days 11-14 (or last 3 days)
  EXPIRED: 'expired',        // After trial ends
  PAID: 'paid',              // On paid plan
};

/**
 * TrialStatus Component
 *
 * A phase-based trial conversion component that:
 * - Shows different UI based on trial phase (early, mid, ending, expired)
 * - Displays value metrics (leads captured, estimated revenue)
 * - Uses psychological triggers (loss aversion, anchoring, scarcity)
 * - Shows discount CTAs only in final days (proven conversion strategy)
 */
const TrialStatus = ({
  trialStartedAt,
  totalTrialDays = 14, // Shopify subscription trial is 14 days
  isPaidUser = false,
  leadsCaptured = 0,
  totalConversions = 0,
  conversionRate = 0,
  averageOrderValue = 45, // Default AOV for projection calculations
  onUpgrade,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();
  const [dismissedPhase, setDismissedPhase] = useState(null);

  // Load persisted state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDismissedPhase(parsed.lastDismissedPhase);
      }
    } catch (e) {
      console.error('Error loading trial status state:', e);
    }
  }, []);

  // Calculate trial status
  const getTrialStatus = useCallback(() => {
    if (isPaidUser) {
      return { phase: TRIAL_PHASES.PAID, daysRemaining: null, daysElapsed: 0, percentUsed: 0 };
    }

    if (!trialStartedAt) {
      return { phase: TRIAL_PHASES.EARLY, daysRemaining: totalTrialDays, daysElapsed: 0, percentUsed: 0 };
    }

    const now = new Date();
    const trialStart = new Date(trialStartedAt);
    const trialEnd = new Date(trialStart);
    trialEnd.setDate(trialEnd.getDate() + totalTrialDays);

    const daysElapsed = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
    const percentUsed = Math.min(100, Math.round((daysElapsed / totalTrialDays) * 100));

    let phase;
    if (daysRemaining <= 0) {
      phase = TRIAL_PHASES.EXPIRED;
    } else if (daysRemaining <= 3) {
      phase = TRIAL_PHASES.ENDING;
    } else if (daysElapsed < 3) {
      phase = TRIAL_PHASES.EARLY;
    } else {
      phase = TRIAL_PHASES.MID;
    }

    return { phase, daysRemaining, daysElapsed, percentUsed, trialEnd };
  }, [trialStartedAt, totalTrialDays, isPaidUser]);

  const trialStatus = getTrialStatus();

  // ⚠️ UNVERIFIED MERCHANT-FACING REVENUE FIGURE — both constants are invented.
  //
  // `averageOrderValue` defaults to 45 (see the prop above) and NO call site has ever
  // overridden it; the number arrived as placeholder copy in commit 33fe4989
  // ("App Home Redesign…"), a UI pass, and was duplicated into the now-dead
  // web/utils/trialUtils.js:94,106. The 0.1 attribution rate has no stated basis and is
  // optimistic for email-capture cohorts. So this renders an assumed AOV times an
  // assumed conversion rate, presented to merchants as "~$X earned".
  //
  // This was harmless while `totalConversions` was permanently 0 (it was fed from
  // `shop.monthlyEmailCaptures`, a field that does not exist). Fixing that count on
  // 2026-08-16 ACTIVATED this figure — a shop with 16 conversions now shows ~$72.
  // Kept deliberately, to be revisited (decision recorded 2026-08-16).
  //
  // Why it cannot simply be made real yet, measured against production so nobody has to
  // re-investigate:
  //   • Every shopifyOrder price field is null — totalPrice, currentTotalPrice,
  //     subtotalPrice, currency. Order financials are not synced, so a genuine AOV needs
  //     a Shopify scope/sync change, not a code change.
  //   • The only other source, api/helpers/attributionAnalyticsHelpers.js:117
  //     (attributedAOV), reads PostHog — which silently substitutes MOCK DATA when empty
  //     (api/routes/GET-shop-analytics.js:174-180). Demo revenue here would be worse
  //     than none.
  const estimatedRevenue = Math.round(totalConversions * averageOrderValue * 0.1);

  // Calculate projected monthly value
  const projectedMonthlyLeads = trialStatus.daysElapsed > 0
    ? Math.round((leadsCaptured / trialStatus.daysElapsed) * 30)
    : 0;
  const projectedMonthlyRevenue = Math.round(projectedMonthlyLeads * averageOrderValue * 0.1);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const current = stored ? JSON.parse(stored) : {};
      const newState = { ...current, lastDismissedPhase: trialStatus.phase };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      setDismissedPhase(trialStatus.phase);
      if (onDismiss) onDismiss();
    } catch (e) {
      console.error('Error saving trial status state:', e);
    }
  }, [trialStatus.phase, onDismiss]);

  // Handle upgrade click
  const handleUpgrade = useCallback(() => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/billing');
    }
  }, [onUpgrade, navigate]);

  // Don't show for paid users
  if (trialStatus.phase === TRIAL_PHASES.PAID) {
    return null;
  }

  // Don't show if dismissed for current phase
  if (dismissedPhase === trialStatus.phase && trialStatus.phase !== TRIAL_PHASES.EXPIRED) {
    return null;
  }

  // Phase-specific rendering
  const renderEarlyTrial = () => (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="block" gap="small-100">
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <s-heading>
                Welcome to Your {totalTrialDays}-Day Trial!
              </s-heading>
              <s-badge tone="success">Day {trialStatus.daysElapsed + 1}</s-badge>
            </s-stack>
            <s-paragraph color="subdued">
              Complete setup to maximize your trial experience
            </s-paragraph>
          </s-stack>
          <s-button onClick={handleDismiss} variant="tertiary">
            Dismiss
          </s-button>
        </s-stack>

        <ProgressBar
          progress={trialStatus.percentUsed}
          tone="primary"
          size="small"
        />

        <s-stack direction="inline" gap="base">
          <s-button onClick={handleUpgrade}>
            See What's Included
          </s-button>
          <s-button variant="tertiary" onClick={() => navigate('/templates')}>
            Get Started
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  );

  const renderMidTrial = () => (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="block" gap="small-100">
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <s-heading>
                Your Nudgesmart Impact So Far
              </s-heading>
              <s-badge tone="info">{trialStatus.daysRemaining} days left</s-badge>
            </s-stack>
          </s-stack>
          <s-button onClick={handleDismiss} variant="tertiary">
            Dismiss
          </s-button>
        </s-stack>

        {/* Value Metrics */}
        <s-grid gridTemplateColumns="repeat(3, 1fr)" gap="base">
          <s-box
            padding="base"
            background="subdued"
            borderRadius="base"
          >
            <s-stack direction="block" gap="small-100" alignItems="center">
              <s-heading>
                {leadsCaptured}
              </s-heading>
              <s-text color="subdued">
                Leads Captured
              </s-text>
            </s-stack>
          </s-box>
          <s-box
            padding="base"
            background="subdued"
            borderRadius="base"
          >
            <s-stack direction="block" gap="small-100" alignItems="center">
              <s-heading>
                {conversionRate.toFixed(1)}%
              </s-heading>
              <s-text color="subdued">
                Conversion Rate
              </s-text>
            </s-stack>
          </s-box>
          <s-box
            padding="base"
            background="subdued"
            borderRadius="base"
          >
            <s-stack direction="block" gap="small-100" alignItems="center">
              <s-heading>
                ~{currencySymbol}{estimatedRevenue}
              </s-heading>
              <s-text color="subdued">
                Est. Revenue
              </s-text>
            </s-stack>
          </s-box>
        </s-grid>

        <s-text color="subdued">
          You're on track! Keep building momentum.
        </s-text>

        <s-stack direction="inline" gap="base">
          <s-button onClick={() => navigate('/analytics')}>
            View Full Analytics
          </s-button>
          <s-button variant="primary" onClick={handleUpgrade}>
            Upgrade & Scale
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  );

  const renderEndingSoon = () => (
    <s-banner
      heading={`Only ${trialStatus.daysRemaining} Day${trialStatus.daysRemaining !== 1 ? 's' : ''} Left in Your Trial!`}
      tone="warning"
      dismissible
      onDismiss={handleDismiss}
    >
      <s-stack direction="block" gap="base">
        {/* Value captured - Loss Aversion messaging */}
        <s-paragraph>
          Don't lose your {leadsCaptured} leads and campaign setup.
        </s-paragraph>

        {/* Discount offer (last 3 days) */}
        <s-box
          padding="small-300"
          background="subdued"
          borderRadius="base"
        >
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-badge tone="success">Special Offer</s-badge>
            <s-text type="strong">
              20% OFF your first 3 months
            </s-text>
          </s-stack>
        </s-box>
      </s-stack>
      <s-button slot="secondary-actions" variant="primary" onClick={handleUpgrade}>
        Claim Discount
      </s-button>
      <s-button slot="secondary-actions" onClick={() => navigate('/billing')}>
        Compare Plans
      </s-button>
    </s-banner>
  );

  const renderExpired = () => (
    <s-banner
      heading="Your Trial Has Ended"
      tone="critical"
    >
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Your campaigns are paused, but your data is safe for 30 days.
          Reactivate now to continue growing your store.
        </s-paragraph>

        {/* Value achieved during trial */}
        {leadsCaptured > 0 && (
          <s-paragraph>
            You captured <strong>{leadsCaptured} leads</strong> worth an estimated <strong>{currencySymbol}{estimatedRevenue}</strong>.
            Don't let this momentum go to waste!
          </s-paragraph>
        )}
      </s-stack>
      <s-button slot="secondary-actions" variant="primary" onClick={handleUpgrade}>
        Reactivate - Special Offer
      </s-button>
      <s-button slot="secondary-actions" onClick={() => navigate('/billing')}>
        View Plans
      </s-button>
    </s-banner>
  );

  // Render based on phase
  switch (trialStatus.phase) {
    case TRIAL_PHASES.EARLY:
      return renderEarlyTrial();
    case TRIAL_PHASES.MID:
      return renderMidTrial();
    case TRIAL_PHASES.ENDING:
      return renderEndingSoon();
    case TRIAL_PHASES.EXPIRED:
      return renderExpired();
    default:
      return null;
  }
};

export default TrialStatus;

// Export phases for external use
export { TRIAL_PHASES };
